import { memo, useMemo } from "react";

import { formatDate } from "@/lib/dayjs/format-date";
import { isPastDate } from "@/lib/dayjs/is-past-date";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";
import type * as FormModelType from "@/components/features/cancellation/model/type";

import type { FormOptionType } from "@/components/ui/form/_types/type";

import { CancellationMainDone, CancellationMainQuestionForm } from "./components";

import type { CheckedState } from "@radix-ui/react-checkbox";

type Props = {
	validDtEndBasic?: string;
	isStopRecurringBasicBilling: boolean;
	churnInfo: ForAutoGenerator<
		{
			reason: string;
			comment?: string | null | undefined;
			alternativeSystemProductName?: string | null | undefined;
			alternativeSystemReason?: string | null | undefined;
		},
		FormModelType.ExcludeKeys
	>;
	hasError: boolean;

	onChangeReasonOfChurnInfo: (checked: CheckedState, item: FormOptionType) => void;
	onChangeCommentOfChurnInfo: (value: string) => void;
	onChangeAlternativeSystemProductName: (value: string) => void;
	onChangeAlternativeSystemReason: (value: string) => void;
	onClickCancellation: () => Promise<void>;
};

export const CancellationMain = memo(
	({
		validDtEndBasic,
		isStopRecurringBasicBilling,
		churnInfo,
		hasError,

		onChangeCommentOfChurnInfo,
		onChangeReasonOfChurnInfo,
		onChangeAlternativeSystemProductName,
		onChangeAlternativeSystemReason,
		onClickCancellation,
	}: Props) => {
		// 基本料金のチケットが有効期限切れかどうか
		const isValidDtEndBasic = useMemo(() => isPastDate(validDtEndBasic || ""), [validDtEndBasic]);
		const formattedDtEndBasic = useMemo(
			() => (validDtEndBasic && !isValidDtEndBasic ? formatDate(validDtEndBasic, "YYYY年MM月DD日") : undefined),
			[isValidDtEndBasic, validDtEndBasic],
		);

		const canShowDone = useMemo(() => isStopRecurringBasicBilling, [isStopRecurringBasicBilling]);

		return (
			<div className="rounded-lg border border-zinc-100 bg-white p-4 lg:p-12">
				{canShowDone ? (
					<CancellationMainDone basicEndDate={formattedDtEndBasic} />
				) : (
					<CancellationMainQuestionForm
						dtEndBasic={formattedDtEndBasic}
						reason={churnInfo.reason}
						comment={churnInfo.comment}
						alternativeSystemProductName={churnInfo.alternativeSystemProductName}
						alternativeSystemReason={churnInfo.alternativeSystemReason}
						disabled={hasError}
						onChangeReasonOfChurnInfo={onChangeReasonOfChurnInfo}
						onChangeCommentOfChurnInfo={onChangeCommentOfChurnInfo}
						onChangeAlternativeSystemProductName={onChangeAlternativeSystemProductName}
						onChangeAlternativeSystemReason={onChangeAlternativeSystemReason}
						onClickCancellation={onClickCancellation}
					/>
				)}
			</div>
		);
	},
);
